import { AGENT_EVENTS, CLIENT_EVENTS } from "./agentContract";

/**
 * Shared engine for the client-side scripted transports (the demo mock and
 * the onboarding/digest flows). It implements the AgentTransport contract
 * (connect/send/interrupt) over an ordered list of steps
 *   { id, expects(clientEvent, ctx) -> bool, run(api, clientEvent, ctx) }
 * and emits the exact AGENT_EVENTS the live SSE transport delivers, so the
 * same BlockRenderer/applyAgentEvent path renders everything.
 *
 * Subclasses supply steps + ctx + copy via the constructor config:
 *   { steps, ctx, fallbackText, onConnected, offScriptHandler }
 * A step may return `false` from run() to stay available (retry beats).
 *
 * `offScriptHandler(api, clientEvent, ctx)` (optional): handles a free-text
 * USER_MESSAGE that no step consumed. Onboarding uses it to route the question
 * to the live MCP copilot and then re-emit the current step, so the flow keeps
 * its rails instead of dead-ending on the canned `fallbackText` nudge.
 */

const wait = ms => new Promise(resolve => setTimeout(resolve, ms));

// Thrown from inside a running turn once interrupt() is called; unwinds the
// current step without surfacing as an agent_error (turn_end already emitted).
export class ScriptInterrupted extends Error {}

class ScriptedTransportBase {
  constructor({ steps = [], ctx = {}, fallbackText = "", onConnected, offScriptHandler } = {}) {
    this.onEvent = null;
    this.disconnected = false;
    this.ts = 0;
    this.turnSeq = 0;
    this.blockSeq = 0;
    this.consumed = {};
    this.ctx = ctx;
    this.steps = steps;
    this.fallbackText = fallbackText;
    this._onConnected = onConnected;
    this._offScriptHandler = offScriptHandler;
    this.interrupted = false;
  }

  connect = (conversationId, onEvent) => {
    this.conversationId = conversationId;
    this.onEvent = onEvent;
    this.disconnected = false;
    if (typeof this._onConnected === "function") {
      Promise.resolve()
        .then(() => this._onConnected(this.api(), this.ctx))
        .catch(err => {
          if (err instanceof ScriptInterrupted) return;
          this.emit(AGENT_EVENTS.AGENT_ERROR, {
            message: err?.message || "Something went wrong."
          });
        });
    }
    return () => {
      this.disconnected = true;
      this.onEvent = null;
    };
  };

  send = clientEvent => {
    if (this.disconnected || !clientEvent) return;
    this.interrupted = false;
    this.process(clientEvent).catch(err => {
      if (err instanceof ScriptInterrupted) return;
      this.emit(AGENT_EVENTS.AGENT_ERROR, {
        message: err?.message || "Something went wrong."
      });
    });
  };

  interrupt = () => {
    this.interrupted = true;
  };

  process = async clientEvent => {
    const step = this.steps.find(
      item => !this.consumed[item.id] && item.expects(clientEvent, this.ctx)
    );
    if (step) {
      this.consumed[step.id] = true;
      const result = await step.run(this.api(), clientEvent, this.ctx);
      if (result === false) this.consumed[step.id] = false;
      return;
    }
    if (clientEvent.type !== CLIENT_EVENTS.USER_MESSAGE) return;
    // Free text no step consumed: prefer the off-script handler (onboarding
    // routes it to the live MCP copilot, then re-emits the current step); fall
    // back to the canned nudge (the demo mock) when no handler is set.
    if (typeof this._offScriptHandler === "function") {
      await this._offScriptHandler(this.api(), clientEvent, this.ctx);
      return;
    }
    if (this.fallbackText) {
      const turn = this.beginTurn();
      await turn.text(this.fallbackText);
      turn.end("user_input");
    }
  };

  emit = (type, payload = {}) => {
    if (this.disconnected || !this.onEvent) return;
    this.ts += 1;
    this.onEvent({ type, ts: this.ts, ...payload });
  };

  api = () => ({
    wait,
    beginTurn: this.beginTurn,
    ctx: this.ctx
  });

  beginTurn = () => {
    const turnId = `t_${(this.turnSeq += 1)}`;
    this.emit(AGENT_EVENTS.TURN_START, { turnId });

    const openBlock = (type, payload = {}) => {
      const block = { id: `b_${(this.blockSeq += 1)}`, type, ...payload };
      this.emit(AGENT_EVENTS.BLOCK_START, { turnId, block });
      return block.id;
    };

    const closeBlock = blockId =>
      this.emit(AGENT_EVENTS.BLOCK_END, { turnId, blockId });

    const patch = (blockId, blockPatch) =>
      this.emit(AGENT_EVENTS.BLOCK_UPDATE, { turnId, blockId, patch: blockPatch });

    return {
      id: turnId,
      openBlock,
      closeBlock,
      patch,
      addBlock: (type, payload = {}) => {
        const blockId = openBlock(type, payload);
        closeBlock(blockId);
        return blockId;
      },
      text: async markdown => {
        const blockId = openBlock("text", { markdown: "" });
        const tokens = (markdown || "").split(/(\s+)/).filter(Boolean);
        let buffer = "";
        for (let i = 0; i < tokens.length; i += 1) {
          if (this.interrupted) {
            closeBlock(blockId);
            this.emit(AGENT_EVENTS.TURN_END, {
              turnId,
              await: "user_input",
              hint: null
            });
            throw new ScriptInterrupted();
          }
          buffer += tokens[i];
          const flush = buffer.length > 6 || i === tokens.length - 1;
          if (flush) {
            this.emit(AGENT_EVENTS.BLOCK_DELTA, { turnId, blockId, delta: buffer });
            buffer = "";
            await wait(20 + ((i % 3) * 8));
          }
        }
        closeBlock(blockId);
        return blockId;
      },
      end: (awaitState = null, hint = null) =>
        this.emit(AGENT_EVENTS.TURN_END, { turnId, await: awaitState, hint })
    };
  };
}

export { wait };
export default ScriptedTransportBase;
